import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Share2 } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { formatDisplayDate } from "@/lib/dateFormat";
import { sanitizeRichTextHtml } from "@/lib/htmlSanitizer";
import { toWordPreview } from "@/lib/textPreview";
import { buildShareUrl } from "@/lib/shareUrl";
import { shareContent } from "@/lib/shareContent";

const NewsList = () => {
  const { news } = useData();
  const [category, setCategory] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Keep common categories first, then include all categories found in data.
  const categoryOptions = Array.from(
    new Set(["All", "English", "Telugu", ...news.map((item) => item.category).filter(Boolean)])
  );
  const categoryFiltered =
    category === "All" ? news : news.filter((n) => n.category === category);
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filtered = !normalizedSearch
    ? categoryFiltered
    : categoryFiltered.filter((item) => {
        const searchHaystack = [
          item.title,
          item.summary,
          item.content,
          item.author,
          item.category,
          item.date,
          formatDisplayDate(item.date),
        ]
          .join(" ")
          .toLowerCase();

        return searchHaystack.includes(normalizedSearch);
      });

  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedNews = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const handleShareNews = async (item: (typeof news)[number]) => {
    const shareUrl = buildShareUrl("news", item.slug || item.id);
    const shareText = toWordPreview(sanitizeRichTextHtml(item.summary || item.content || ""), 20);

    setShareMessage("");
    const result = await shareContent({
      title: item.title,
      text: shareText,
      url: shareUrl,
      imageUrl: item.image,
    });

    if (result.status === "copied") {
      setShareMessage("News link copied to clipboard.");
      return;
    }

    if (result.status === "unsupported") {
      setShareMessage("Sharing is not supported in this browser.");
      return;
    }

    if (result.status === "error") {
      setShareMessage("Unable to share this news right now.");
    }
  };

  return (
    <div className="py-6 sm:py-10 md:py-14">
      <div className="container mx-auto px-4">
        <h1 className="section-title mb-2 text-xl sm:text-2xl md:text-3xl">Latest News</h1>
        <p className="text-muted-foreground mb-6 sm:mb-8 max-w-xl text-sm sm:text-base">Latest news, IPO updates, corporate announcements, tech news, and financial market insights.</p>

        <form onSubmit={onSearchSubmit} className="mb-6 sm:mb-8 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by title, category, author, date..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
          >
            <Search size={15} />
            Search
          </button>
        </form>

        <div className="flex gap-1 sm:gap-2 mb-6 sm:mb-8 flex-wrap">
          {categoryOptions.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setCategory(cat);
                setCurrentPage(1);
              }}
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${category === cat ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 items-start">
          {paginatedNews.map((n, i) => (
            <motion.div key={n.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <div className="finance-card p-3 sm:p-4 md:p-5 flex flex-col">
                <Link to={`/news/${n.slug || n.id}`} className="block">
                  <span className="badge-info mb-3 text-xs">{n.category}</span>
                  
                  {/* News Image */}
                  {n.image && (
                    <div className="mb-3">
                      <img 
                        src={n.image} 
                        alt={n.title}
                        className="w-full h-32 sm:h-40 object-cover rounded-lg"
                        onError={(e) => {
                          console.log("News image failed to load:", n.image);
                          e.currentTarget.src = "/placeholder.svg";
                        }}
                      />
                    </div>
                  )}
                  
                  <h3 className="font-display font-semibold text-foreground mb-2 line-clamp-2 text-sm sm:text-base">{n.title}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-4">
                    {toWordPreview(sanitizeRichTextHtml(n.summary), 20)}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="text-xs">{n.author}</span><span className="text-xs">{formatDisplayDate(n.date)}</span>
                  </div>
                </Link>
                <div className="mt-3 pt-3 border-t border-border flex justify-end">
                  <button
                    type="button"
                    onClick={() => void handleShareNews(n)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                  >
                    <Share2 size={14} />
                    Share
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {shareMessage && <div className="mt-2 text-sm text-muted-foreground">{shareMessage}</div>}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No news found for this search/filter.
          </div>
        )}
        {filtered.length > 0 && (
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
                disabled={safePage === 1}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 rounded-lg text-xs border ${
                    safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
                disabled={safePage === totalPages}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        <div className="mt-8 p-4 rounded-lg bg-muted/50 border border-border">
          <p className="text-xs text-muted-foreground leading-relaxed"><strong>Disclaimer:</strong> News articles are for informational purposes only. We do not guarantee completeness or accuracy.</p>
        </div>
      </div>
    </div>
  );
};

export default NewsList;
